import { useState } from 'react'
import {
  Card, Table, Button, Space, Typography, Select, Tag,
  Drawer, Form, Input, message, Row, Col, DatePicker, Popconfirm, InputNumber,
} from 'antd'
import { AlertOutlined, PlusOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title } = Typography
const API = ''

const SEVERITY_COLORS: Record<string, string> = {
  S1: 'red',
  S2: 'orange',
  S3: 'gold',
  S4: 'default',
}

const STATUS_COLORS: Record<string, string> = {
  OPEN: 'processing',
  UNDER_INVESTIGATION: 'warning',
  CLOSED: 'success',
}

const INCIDENT_TYPES = ['THEFT', 'TRESPASS', 'DAMAGE', 'ALTERCATION', 'LOST_PASS', 'SUSPICIOUS', 'FIRE', 'OTHER']
const SEVERITIES = ['S1', 'S2', 'S3', 'S4']
const STATUSES = ['OPEN', 'UNDER_INVESTIGATION', 'CLOSED']

export default function IncidentPage() {
  const { plantId, plants, setPlantId } = usePlantId()
  const qc = useQueryClient()
  const [severityFilter, setSeverityFilter] = useState<string | undefined>()
  const [statusFilter, setStatusFilter] = useState<string | undefined>()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [form] = Form.useForm()

  const { data: incidents, isLoading } = useQuery({
    queryKey: ['security-incidents', plantId, severityFilter, statusFilter],
    queryFn: () =>
      apiClient.get(`${API}/security-incidents`, {
        params: {
          plant_id: plantId,
          severity: severityFilter || undefined,
          status: statusFilter || undefined,
        },
      }).then(r => r.data),
    enabled: !!plantId,
  })

  const createMut = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post(`${API}/security-incidents`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['security-incidents'] })
      message.success('Incident reported')
      setDrawerOpen(false)
      form.resetFields()
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const closeMut = useMutation({
    mutationFn: (id: string) =>
      apiClient.post(`${API}/security-incidents/${id}/close`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['security-incidents'] })
      message.success('Incident closed')
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const list = Array.isArray(incidents) ? incidents : []

  const columns = [
    {
      title: 'Incident No',
      dataIndex: 'incident_no',
      key: 'incident_no',
      width: 130,
      render: (v: string) => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span>,
    },
    {
      title: 'Date',
      dataIndex: 'incident_date',
      key: 'incident_date',
      width: 110,
      render: (v: string) => v ? dayjs(v).format('DD-MMM-YYYY') : '—',
    },
    { title: 'Location', dataIndex: 'location', key: 'location', width: 140 },
    {
      title: 'Type',
      dataIndex: 'incident_type',
      key: 'incident_type',
      width: 120,
      render: (v: string) => <Tag>{v}</Tag>,
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      width: 90,
      render: (v: string) => <Tag color={SEVERITY_COLORS[v] ?? 'default'}>{v}</Tag>,
    },
    { title: 'Reporter', dataIndex: 'reporter_name', key: 'reporter_name', width: 140 },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (v: string) => <Tag color={STATUS_COLORS[v] ?? 'default'}>{v?.replace('_', ' ')}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 130,
      render: (_: unknown, r: any) =>
        (r.status === 'OPEN' || r.status === 'UNDER_INVESTIGATION') ? (
          <Popconfirm
            title="Close this incident?"
            onConfirm={() => closeMut.mutate(r.id)}
            okText="Close"
          >
            <Button size="small" danger>Close Incident</Button>
          </Popconfirm>
        ) : null,
    },
  ]

  const expandedRowRender = (record: any) => (
    <div style={{ padding: '8px 16px', background: '#fafafa', borderRadius: 4 }}>
      <Row gutter={16}>
        <Col span={12}>
          <Typography.Text strong>Brief Facts: </Typography.Text>
          <Typography.Text>{record.brief_facts || '—'}</Typography.Text>
        </Col>
        <Col span={12}>
          <Typography.Text strong>Immediate Action: </Typography.Text>
          <Typography.Text>{record.immediate_action || '—'}</Typography.Text>
        </Col>
        <Col span={8} style={{ marginTop: 8 }}>
          <Typography.Text strong>Estimated Loss: </Typography.Text>
          <Typography.Text>{record.estimated_loss ?? '—'}</Typography.Text>
        </Col>
        <Col span={8} style={{ marginTop: 8 }}>
          <Typography.Text strong>Root Cause: </Typography.Text>
          <Typography.Text>{record.root_cause || '—'}</Typography.Text>
        </Col>
        <Col span={8} style={{ marginTop: 8 }}>
          <Typography.Text strong>Corrective Action: </Typography.Text>
          <Typography.Text>{record.corrective_action || '—'}</Typography.Text>
        </Col>
      </Row>
    </div>
  )

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <AlertOutlined style={{ marginRight: 8, color: '#ff4d4f' }} />
        Security Incidents
      </Title>
      <Typography.Text type="secondary">Report and track security incidents</Typography.Text>

      <Card style={{ marginTop: 16 }}>
        <Row gutter={12} style={{ marginBottom: 16 }} justify="space-between">
          <Col>
            <Space wrap>
              <Select
                value={plantId}
                onChange={setPlantId}
                style={{ width: 200 }}
                options={plants.map((p: any) => ({ value: p.id, label: p.name ?? p.plant_name }))}
              />
              <Select
                allowClear
                placeholder="Severity"
                style={{ width: 110 }}
                value={severityFilter}
                onChange={v => setSeverityFilter(v)}
                options={SEVERITIES.map(s => ({ value: s, label: s }))}
              />
              <Select
                allowClear
                placeholder="Status"
                style={{ width: 160 }}
                value={statusFilter}
                onChange={v => setStatusFilter(v)}
                options={STATUSES.map(s => ({ value: s, label: s.replace('_', ' ') }))}
              />
            </Space>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => { form.resetFields(); setDrawerOpen(true) }}
            >
              Report Incident
            </Button>
          </Col>
        </Row>

        <Table
          dataSource={list}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          size="small"
          pagination={{ pageSize: 50 }}
          scroll={{ x: 1000 }}
          expandable={{ expandedRowRender }}
        />
      </Card>

      <Drawer
        title="Report Security Incident"
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); form.resetFields() }}
        width={500}
        footer={
          <Row justify="end">
            <Space>
              <Button onClick={() => { setDrawerOpen(false); form.resetFields() }}>Cancel</Button>
              <Button
                type="primary"
                loading={createMut.isPending}
                onClick={() =>
                  form.validateFields().then(vals => {
                    const payload = {
                      ...vals,
                      plant_id: plantId,
                      incident_date: vals.incident_date
                        ? vals.incident_date.format('YYYY-MM-DD')
                        : undefined,
                    }
                    createMut.mutate(payload)
                  })
                }
              >
                Submit
              </Button>
            </Space>
          </Row>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item name="incident_date" label="Incident Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="location" label="Location" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Row gutter={12}>
            <Col span={14}>
              <Form.Item name="incident_type" label="Incident Type" rules={[{ required: true }]}>
                <Select options={INCIDENT_TYPES.map(t => ({ value: t, label: t }))} />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item name="severity" label="Severity" rules={[{ required: true }]}>
                <Select options={SEVERITIES.map(s => ({ value: s, label: s }))} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="reporter_name" label="Reporter Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="persons_involved" label="Persons Involved">
            <Input />
          </Form.Item>
          <Form.Item name="brief_facts" label="Brief Facts" rules={[{ required: true }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="immediate_action" label="Immediate Action Taken">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="estimated_loss" label="Estimated Loss (₹)">
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="evidence_details" label="Evidence Details">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  )
}
